'use strict';

import { getCurrentSearcherObj, getCurrentSearchOptionType } from '@core/utils/searcher';
import { STORE_SEARCHER_FILTERS } from '@core/constants/STORE';
import { SEARCHER_TYPES } from '@core/constants/SEARCHER';
import { SUB_STRING_CODE } from '@core/constants/CONSTANTS';
import { updateStoreFiltersState } from '@core/utils/store';
import { DATA_TRANSLATE_ATTRIBUTE } from '@core/constants/ATTRIBUTES';

/**
 * Format number for display with k/M suffixes
 * @param {number|string} number - The number to format
 * @returns {string} - The formatted number
 */
export const formatNumberForDisplay = (number) => {
    if (number === '-') return '-';

    const num = Number(number);

    if (num >= 1000 && num < 100000) {
        return `${Math.round(num / 100) / 10}k`;
    } else if (num >= 100000 && num < 1000000) {
        return `${Math.round(num / 1000)}k`;
    } else if (num >= 1000000) {
        return `${Math.round(num / 100000) / 10}M`;
    }

    return String(num);
};

/**
 * Update tab button states (active/inactive)
 * @param {NodeListOf<HTMLElement>} imagesTabButtons - Images tab buttons
 * @param {NodeListOf<HTMLElement>} reportsTabButtons - Reports tab buttons
 * @param {boolean} isImagesActive - Whether images tab should be active
 * @returns {void}
 */
export const updateTabButtonStates = (
    imagesTabButtons,
    reportsTabButtons,
    isImagesActive,
) => {
    imagesTabButtons.forEach((tabButton) => {
        if (isImagesActive) {
            tabButton.classList.add('active-tab');
        } else {
            tabButton.classList.remove('active-tab');
        }
    });

    reportsTabButtons.forEach((tabButton) => {
        if (isImagesActive) {
            tabButton.classList.remove('active-tab');
        } else {
            tabButton.classList.add('active-tab');
        }
    });
};

/**
 * Update total results display elements
 * @param {NodeListOf<HTMLElement>} elements - The elements to update
 * @param {string} value - The value to set
 * @param {boolean} clickOnTab - Whether this was triggered by a tab click
 * @returns {void}
 */
export const updateTotalResultsDisplay = (elements, value, clickOnTab) => {
    elements.forEach((element) => {
        if (clickOnTab && value === '-') {
            return;
        }
        element.innerHTML = value;
    });
};

/**
 * Update store with results data
 * @param {string} totalImgs - Formatted total images
 * @param {string} totalReports - Formatted total reports
 * @param {boolean} clickOnTab - Whether this was triggered by a tab click
 * @returns {void}
 */
export const updateStoreWithResults = (totalImgs, totalReports, clickOnTab) => {
    const currentFiltersStateHistory = getCurrentSearcherObj();
    const urlParams = new URLSearchParams(window.location.search);

    if (!currentFiltersStateHistory) return;

    if (!(clickOnTab && totalImgs === '-')) {
        currentFiltersStateHistory[STORE_SEARCHER_FILTERS.TOTAL_IMGS] = totalImgs;
    }

    if (!(clickOnTab && totalReports === '-')) {
        currentFiltersStateHistory[STORE_SEARCHER_FILTERS.TOTAL_REPORTS] = totalReports;
    }

    updateStoreFiltersState(
        urlParams.get(SEARCHER_TYPES.SEARCHER_ID),
        currentFiltersStateHistory,
    );
};

/**
 * Update search option translation for images buttons
 * @param {NodeListOf<HTMLElement>} imagesButtons - The images buttons to update
 * @returns {void}
 */
export const updateSearchOptionTranslation = (imagesButtons) => {
    const searchOption = getCurrentSearchOptionType();

    imagesButtons.forEach((button) => {
        const spanElement = button.querySelector(`[${DATA_TRANSLATE_ATTRIBUTE}]`);
        if (spanElement) {
            spanElement.setAttribute(
                DATA_TRANSLATE_ATTRIBUTE,
                `results.tabs.${searchOption.includes(SUB_STRING_CODE.VIDEOS) ? SUB_STRING_CODE.VIDEOS.toLowerCase() : 'images'}`,
            );
        }
    });
};
